import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { actions, selectors } from "@containers/Cases/store";
import { CASE_DOCUMENT_STATUS, CASE_FILE_KIND, PROCESSING_CASE_STATUSES } from "@shared/models";

const TIMEOUT = 5000; //5 sec

const useLoadCaseInfo = (caseId: number) => {
  const dispatch = useDispatch();
  const caseObj = useSelector(selectors.getCase());
  const caseDocuments = useSelector(selectors.getCaseDocuments());

  const handleFetchCaseInfo = useCallback(() => {
    dispatch(actions.getCase.request({ caseId: Number(caseId) }));
  }, [caseId, dispatch]);

  const caseStatus = caseObj?.status;

  useEffect(() => {
    if (!caseStatus || !PROCESSING_CASE_STATUSES.includes(caseStatus)) return;

    const intervalId = setInterval(() => {
      dispatch(actions.getCase.request({ caseId: Number(caseId), isLoadingBlocked: true }));
    }, TIMEOUT);

    return () => clearInterval(intervalId);
  }, [caseId, caseStatus, dispatch]);

  // The PSR is refreshed with the case above; medical summaries and case notes parse on their own.
  const isParsingDocuments = caseDocuments.some(
    ({ kind, status }) => kind !== CASE_FILE_KIND.PSR && status === CASE_DOCUMENT_STATUS.PARSING,
  );

  useEffect(() => {
    if (!isParsingDocuments) return;

    const intervalId = setInterval(() => {
      dispatch(actions.getCaseDocuments.request(Number(caseId)));
    }, TIMEOUT);

    return () => clearInterval(intervalId);
  }, [caseId, isParsingDocuments, dispatch]);

  useEffect(() => {
    handleFetchCaseInfo();
  }, [handleFetchCaseInfo]);

  useEffect(() => {
    return () => {
      dispatch(actions.getCase.success(null));
    };
  }, [dispatch]);

  return {
    caseObj,
    handleFetchCaseInfo,
  };
};

export default useLoadCaseInfo;
